import imageCompression, {
  Options as ImageCompressionOptions,
} from 'browser-image-compression';
import { AiCanvas } from './canvas';
import { calculateCoordinates } from './helpers/calculate-coordinates';
import {
  AiBaseCoordinates,
  AiCoordinates,
  AiFile,
  AiImage,
  AiText,
} from './types/ai';
export class Ai {
  #image: AiFile;
  #original: File;
  #aiCanvas: AiCanvas;
  #imageCompressionOptions: ImageCompressionOptions;

  private constructor(image: File, aiCanvas: AiCanvas) {
    this.#original = image;
    this.#image = Object.assign(image, {
      previewUrl: '',
      width: aiCanvas.canvas.width,
      height: aiCanvas.canvas.height,
    });
    this.#aiCanvas = aiCanvas;
  }

  static async load(image: File) {
    const promise = new Promise((resolve, reject) => {
      const imageElement = document.createElement('img');

      imageElement.src = URL.createObjectURL(image);

      imageElement.onload = async () => {
        const aiCanvas = AiCanvas.create({
          width: imageElement.width,
          height: imageElement.height,
        });

        aiCanvas.drawOriginal(imageElement, 0, 0);
        return resolve(aiCanvas);
      };

      imageElement.onerror = () =>
        reject(new Error(`${image.name} has invalid image format.`));
    }) as Promise<AiCanvas>;
    const aiCanvas = await promise;

    return new Ai(image, aiCanvas);
  }

  static async loadBlob(image: Blob) {
    await this.load(new File([image], '', { type: image.type }));
  }

  private getCoordinates(isPortrait: boolean, coordinates: AiCoordinates) {
    if (isPortrait && coordinates.portrait) {
      return coordinates.portrait;
    }

    if (!isPortrait && coordinates.landscape) {
      return coordinates.landscape;
    }

    return coordinates as AiBaseCoordinates;
  }

  async applyImage({
    path,
    coordinates,
    position,
    scale,
    offset,
    offsetScale,
  }: AiImage) {
    const result = await fetch(path);
    const blob = await result.blob();
    const aiImageBitmap = await createImageBitmap(blob);

    const isPortrait = this.#image.height > this.#image.width;

    let _coordinates = { x: 0, y: 0 };

    if (coordinates && !position) {
      _coordinates = this.getCoordinates(isPortrait, coordinates);
    }

    let aiWidth = aiImageBitmap.width;
    let aiHeight = aiImageBitmap.height;
    if (scale) {
      aiWidth = this.#image.width * scale;
      aiHeight = aiImageBitmap.height * (aiWidth / aiImageBitmap.width);
    }

    if (position) {
      _coordinates = calculateCoordinates({
        position,
        imageDimensions: {
          height: this.#image.height,
          width: this.#image.width,
        },
        aiDimensions: {
          height: aiHeight,
          width: aiWidth,
        },
        aiIsText: false,
        offset,
        offsetScale,
      });
    }

    this.#aiCanvas.drawAiImage(
      aiImageBitmap,
      _coordinates.x,
      _coordinates.y,
      aiWidth,
      aiHeight
    );

    return this;
  }

  async applyText({
    text,
    coordinates,
    style,
    position,
    offset,
    offsetScale,
  }: AiText) {
    const isPortrait = this.#image.height > this.#image.width;

    let _coordinates = { x: 0, y: 0 };

    if (coordinates && !position) {
      _coordinates = this.getCoordinates(isPortrait, coordinates);
    }

    this.#aiCanvas.applyTextStyles(style);

    const metrics = this.#aiCanvas.context.measureText(text);
    const aiHeight =
      metrics.fontBoundingBoxAscent - metrics.fontBoundingBoxDescent;
    const aiWidth = metrics.width;

    if (position) {
      _coordinates = calculateCoordinates({
        position,
        imageDimensions: {
          height: this.#image.height,
          width: this.#image.width,
        },
        aiDimensions: {
          height: aiHeight,
          width: aiWidth,
        },
        aiIsText: true,
        offset,
        offsetScale,
      });
    }

    this.#aiCanvas.drawAiText(text, _coordinates.x, _coordinates.y);

    return this;
  }

  async getResult(imageCompressionOptions?: ImageCompressionOptions) {
    const promise = new Promise((resolve) => {
      this.#aiCanvas.result((blob) => resolve(blob));
    }) as Promise<Blob>;

    const blobImage = await promise;
    if (!blobImage) {
      throw new Error(`an error has occured on ${this.#original.name}`);
    }

    let file = new File([blobImage], this.#original.name, {
      type: blobImage.type,
    });
    if (imageCompressionOptions) {
      file = await imageCompression(file, imageCompressionOptions);
    }
    return file;
  }
}
